// Frontend role helpers — mirrors backend `auth._role_satisfies`.
//
// `owner` is the default seed role for new tenants. It combines admin
// + dispatcher capabilities in one login so single-operator tow
// companies don't need to seed multiple back-office accounts. Truck
// is deliberately excluded — it's a physical role (driver in the
// cab) separate from back-office duties.
//
// Sep 2026 — viewer role retired (Nuclear commit). Every viewer-only
// capability collapsed into dispatcher; existing viewer accounts are
// migrated to dispatcher on the next backend startup. Fewer roles =
// simpler onboarding UX for mass-tenant rollout. Jun 2026: any token or
// user record still carrying the dead `viewer` role is normalised to
// `dispatcher` here, so a stale session can never land on a role that no
// route accepts (that bricked a live user with a blank screen).
//
// USAGE — prefer these helpers over `user?.role === "admin"` checks:
//
//   import { hasCap, isAdmin, isDispatcher, isTruck } from "../lib/roles";
//   if (isAdmin(user)) { … }
//   if (hasCap(user, "admin", "dispatcher")) { … }
const OWNER_IMPLIES = new Set(["admin", "dispatcher"]);
const RETIRED_ROLES = { viewer: "dispatcher" };

export function roleOf(user) {
  const r = user?.role;
  if (!r) return null;
  return RETIRED_ROLES[r] || r;
}

export function hasCap(user, ...caps) {
  const r = roleOf(user);
  if (!r) return false;
  if (caps.includes(r)) return true;
  return r === "owner" && caps.some((c) => OWNER_IMPLIES.has(c));
}

export const isAdmin = (u) => hasCap(u, "admin");
export const isDispatcher = (u) => hasCap(u, "dispatcher");
// Truck is NOT implied by owner — driver-flow invariants (GPS lock,
// one-active-job-per-truck) must never be bypassed by an office login.
export const isTruck = (u) => u?.role === "truck";
export const isOwner = (u) => u?.role === "owner";

// Jun 2026 — per-user capability switches. The backend sends the already
// resolved answer on /auth/me and login (role baseline + that person's own
// switches), so this is a plain lookup rather than a second copy of the
// rules — a button and the endpoint behind it can never disagree.
//
//   if (can(user, "archive_jobs")) { …show the Archive button… }
//
// Unknown/absent permissions fall back to true for office roles so a
// stale session from before this shipped doesn't lose its buttons; the
// API is still the real gate.
export function can(user, cap) {
  const perms = user?.permissions;
  if (user?.role === "owner") return true;
  if (!perms || !(cap in perms)) return hasCap(user, "admin", "dispatcher");
  return perms[cap] === true;
}
